import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { Bloco, ConfigPlanner, DocumentoMiolo, Paleta } from '@miolos/core';
import { ehIcone } from '@miolos/core';
import { DocumentoImpressao } from './DocumentoImpressao';

interface Opcoes {
  documento: DocumentoMiolo;
  paleta: Paleta;
  config: ConfigPlanner;
  resolverImagem?: (url: string) => string;
  /** Texto da marca de cópia não autorizada. Nulo = documento liberado. */
  marcaDagua?: string | null;
  marcaDaguaDetalhe?: string | null;
}

/**
 * Espera a imagem ficar pronta PARA PINTAR, não apenas baixada.
 *
 * `decode()` avisa quando o navegador terminou de decodificar o arquivo — é o
 * sinal que interessa antes de abrir o diálogo de impressão, porque uma imagem
 * só baixada ainda pode sair em branco na primeira página. Navegadores antigos
 * não têm `decode()`: nesses o `onload` (que o `src` dispara) resolve a espera.
 */
export function preCarregarImagem(url: string): Promise<void> {
  return new Promise((resolver) => {
    const imagem = new Image();
    imagem.onload = () => {
      if (typeof imagem.decode !== 'function') {
        resolver();
        return;
      }
      imagem.decode().then(
        () => resolver(),
        () => resolver(),
      );
    };
    // Imagem quebrada (arquivo apagado, link externo fora do ar) não pode
    // travar a impressão.
    imagem.onerror = () => resolver();
    imagem.src = url;
  });
}

/**
 * Todas as imagens do documento, sem repetir.
 *
 * São dois caminhos, e os dois precisam ser percorridos: os motivos e as
 * ilustrações de fundo vêm em `pagina.camadas`, enquanto a arte da capa enviada
 * pelo usuário, a contracapa e o caderno de desenho entram como BLOCO
 * (`tipo: 'IMAGEM'`) dentro de uma seção. Olhar só as camadas fazia o diálogo de
 * impressão abrir antes de a capa carregar — e o preview saía sem ela.
 */
export function urlsDeImagem(documento: DocumentoMiolo): string[] {
  const urls = new Set<string>();

  const guardar = (url: string | null | undefined) => {
    // Ícones da biblioteca são SVG embutidos: não há arquivo para esperar.
    if (!url || ehIcone(url)) return;
    urls.add(url);
  };

  const doBloco = (bloco: Bloco) => {
    if (bloco.tipo === 'IMAGEM') guardar(bloco.url);
    // Contêineres: a grade dos hábitos e as caixas guardam blocos dentro.
    if (bloco.tipo === 'GRUPO') bloco.blocos.forEach(doBloco);
    if (bloco.tipo === 'CAIXA') bloco.conteudo?.forEach(doBloco);
  };

  for (const pagina of documento.paginas) {
    for (const camada of pagina.camadas ?? []) guardar(camada.url);
    for (const secao of pagina.secoes ?? []) {
      secao.blocos?.forEach(doBloco);
      for (const coluna of secao.colunas ?? []) coluna.blocos.forEach(doBloco);
    }
  }

  return [...urls];
}

/**
 * Abre o diálogo de impressão do navegador com o miolo inteiro.
 *
 * O documento só é montado no DOM durante a impressão (via portal), para a
 * tela não sofrer com 400 páginas renderizadas de uma vez.
 */
export function useImpressao({
  documento,
  paleta,
  config,
  resolverImagem,
  marcaDagua,
  marcaDaguaDetalhe,
}: Opcoes) {
  const [imprimindo, setImprimindo] = useState(false);

  const imprimir = useCallback(() => setImprimindo(true), []);

  // As capas também são páginas do documento (`paginaDeArte`), então percorrer
  // a lista de páginas já alcança a arte da frente e a do verso.
  const chaveImagens = useMemo(() => urlsDeImagem(documento).join('|'), [documento]);

  useEffect(() => {
    if (!imprimindo) return;
    let cancelado = false;

    const preparar = async () => {
      // Dois frames garantem que o portal foi pintado antes de medir/imprimir.
      await new Promise<void>((resolver) => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolver()));
      });

      // Imagens que não carregarem não podem travar a impressão.
      const urls = chaveImagens ? chaveImagens.split('|') : [];
      await Promise.all(
        urls.map((url) => preCarregarImagem(resolverImagem ? resolverImagem(url) : url)),
      );

      if (typeof document !== 'undefined' && 'fonts' in document) {
        await document.fonts.ready.catch(() => undefined);
      }

      if (cancelado) return;
      window.print();
    };

    const aoTerminar = () => {
      if (!cancelado) setImprimindo(false);
    };

    window.addEventListener('afterprint', aoTerminar);
    void preparar();

    return () => {
      cancelado = true;
      window.removeEventListener('afterprint', aoTerminar);
    };
  }, [imprimindo, chaveImagens, resolverImagem]);

  const areaImpressao: ReactNode =
    imprimindo && typeof document !== 'undefined'
      ? createPortal(
          <div className="area-impressao">
            <DocumentoImpressao
              documento={documento}
              paleta={paleta}
              config={config}
              resolverImagem={resolverImagem}
              marcaDagua={marcaDagua}
              marcaDaguaDetalhe={marcaDaguaDetalhe}
            />
          </div>,
          document.body,
        )
      : null;

  return { imprimindo, imprimir, areaImpressao };
}
